<template>
  <div>
    <div class="appui">
      <!-- LISTA -->
      <div v-if="view === 'list'">
        <div class="appui-head">
          <div>
            <h1 class="appui-title">Agenda</h1>
            <div class="appui-crumb">App <span class="sep">/</span> Agenda</div>
          </div>
        </div>

        <!-- Vazio -->
        <div v-if="!list.length" class="appui-emptystate">
          <div class="appui-emptystate-icon"><i class="bx bx-calendar-event"></i></div>
          <div class="appui-emptystate-title">Nenhuma programação ainda</div>
          <div class="appui-emptystate-sub">Monte a agenda do evento — sessões por dia, com horário, sala e palestrantes.</div>
          <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Adicionar a primeira sessão</button>
        </div>

        <!-- Bloco principal -->
        <section v-else class="appui-card appui-card--flush">
          <div class="appui-card-toolbar">
            <div class="appui-card-toolbar-left">
              <div class="appui-seg" style="flex-wrap:wrap">
                <button v-for="d in daySeg" :key="d.value" :class="{ 'is-active': selectedDay === d.value }" @click="selectDay(d.value)">{{ d.label }}</button>
              </div>
            </div>
            <div class="appui-card-toolbar-right">
              <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Nova sessão</button>
            </div>
          </div>

          <div v-if="!filtered.length" class="appui-inline-empty">Nenhuma sessão neste dia.</div>

          <draggable
            v-else
            v-model="displayList"
            :element="'div'"
            :options="{ animation: 180, handle: '.appui-grip', ghostClass: 'appui-drag-ghost', disabled: isFiltering }"
            @end="onReorder"
          >
            <div v-for="s in displayList" :key="s.id" class="appui-agenda-row">
              <span class="appui-grip" :class="{ 'is-disabled': isFiltering }" :title="isFiltering ? 'Veja \'Todos\' os dias para reordenar' : 'Arraste para reordenar'">⠿</span>
              <div class="appui-agenda-time">
                <div class="appui-agenda-daymo"><strong>{{ dayNum(s.schedule_date) }}</strong> {{ monthAbbr(s.schedule_date) }}</div>
                <div class="appui-agenda-hours">{{ timeRange(s) }}</div>
              </div>
              <div class="appui-agenda-main">
                <div class="appui-agenda-title-row">
                  <span class="appui-agenda-title">{{ s.title }}</span>
                  <span v-if="s.schedule_type" class="appui-badge appui-badge--blue">{{ s.schedule_type }}</span>
                </div>
                <div class="appui-agenda-meta">
                  <span v-if="roomName(s)"><i class="mdi mdi-map-marker"></i> {{ roomName(s) }}</span>
                  <span v-if="s.speakers && s.speakers.length"><i class="mdi mdi-account-voice"></i> {{ s.speakers.map(x => x.name).join(', ') }}</span>
                </div>
              </div>
              <button class="appui-switch" :class="{ 'is-on': s.display }" @click="toggleVis(s)" title="Visível na agenda"><span class="knob"></span></button>
              <button class="appui-tbtn" @click="openEdit(s)">Editar</button>
              <button class="appui-tbtn appui-tbtn--danger" @click="remove(s)">Excluir</button>
            </div>
          </draggable>

          <div v-if="filtered.length" class="appui-card-footer">
            <div class="appui-perpage">
              <span>Mostrar</span>
              <select class="appui-select appui-select--sm" v-model.number="perPage">
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="0">Todos</option>
              </select>
              <span>por vez</span>
            </div>
            <span class="appui-count">Mostrando {{ displayList.length }} de {{ filtered.length }}</span>
          </div>
        </section>
      </div>

      <!-- FORM (página inteira) -->
      <div v-else>
        <div class="appui-head">
          <div>
            <h1 class="appui-title">{{ form.id ? 'Editar sessão' : 'Nova sessão' }}</h1>
            <div class="appui-crumb">App <span class="sep">/</span> Agenda <span class="sep">/</span> {{ form.id ? form.title : 'Nova' }}</div>
          </div>
          <button class="appui-btn appui-btn--ghost" @click="backToList()"><i class="mdi mdi-arrow-left mr-1"></i> Voltar</button>
        </div>

        <section class="appui-card" style="padding:24px">
          <div class="appui-grid-3" style="margin-bottom:16px">
            <div><label class="appui-label">Data *</label><input type="date" class="appui-input" v-model="form.schedule_date"></div>
            <div><label class="appui-label">Início</label><input type="time" class="appui-input" v-model="form.start_time"></div>
            <div><label class="appui-label">Fim</label><input type="time" class="appui-input" v-model="form.end_time"></div>
          </div>
          <div style="margin-bottom:16px"><label class="appui-label">Título *</label><input type="text" class="appui-input" v-model="form.title" placeholder="Ex.: Abertura oficial"></div>
          <div class="appui-grid-2" style="margin-bottom:16px">
            <div>
              <label class="appui-label">Sala</label>
              <select class="appui-input" v-model="form.room_id">
                <option :value="null">— Sem sala —</option>
                <option v-for="r in rooms" :key="r.id" :value="r.id">{{ r.name }}</option>
              </select>
            </div>
            <div>
              <label class="appui-label">Tipo</label>
              <input type="text" class="appui-input" v-model="form.schedule_type" placeholder="Palestra, Workshop..." list="agenda-types">
              <datalist id="agenda-types"><option v-for="t in typeSuggestions" :key="t" :value="t"></option></datalist>
            </div>
          </div>
          <div style="margin-bottom:16px"><label class="appui-label">Descrição</label><textarea class="appui-input" style="min-height:90px" v-model="form.description"></textarea></div>

          <label class="appui-label">Palestrantes</label>
          <div class="appui-speaker-picker">
            <p v-if="!speakers.length" class="appui-sec-sub" style="margin:0">Nenhum palestrante cadastrado ainda.</p>
            <label v-for="sp in speakers" :key="sp.id" class="appui-speaker-chip" :class="{ on: form.speakers.includes(sp.id) }">
              <input type="checkbox" :value="sp.id" v-model="form.speakers" style="display:none">
              <i class="mdi" :class="form.speakers.includes(sp.id) ? 'mdi-check-circle' : 'mdi-plus-circle-outline'"></i> {{ sp.name }}
            </label>
          </div>

          <div style="display:flex;justify-content:space-between;align-items:center;border-top:1px solid #eff2f7;padding-top:18px;margin-top:20px">
            <div style="display:flex;align-items:center;gap:10px">
              <span style="font-size:13px;color:#495057;font-weight:500">Visível na agenda</span>
              <button class="appui-switch" :class="{ 'is-on': form.display }" @click="form.display = form.display ? 0 : 1"><span class="knob"></span></button>
            </div>
            <div style="display:flex;gap:10px">
              <button class="appui-btn appui-btn--ghost" @click="backToList()">Cancelar</button>
              <button class="appui-btn appui-btn--primary" :disabled="saving" @click="save()">Salvar sessão</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

const empty = () => ({ id: null, schedule_date: '', start_time: '', end_time: '', title: '', room_id: null, schedule_type: '', description: '', display: 1, speakers: [] })
const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

export default {
  components: { draggable },
  name: 'app-agenda',
  data () {
    return {
      view: 'list',
      list: [],
      speakers: [],
      rooms: [],
      saving: false,
      form: empty(),
      selectedDay: 'all',
      perPage: 50,
      typeSuggestions: ['Palestra', 'Workshop', 'Painel', 'Mesa-redonda', 'Keynote', 'Intervalo', 'Almoço', 'Credenciamento', 'Networking']
    }
  },
  computed: {
    days () { return [...new Set(this.list.map(s => s.schedule_date))].filter(Boolean).sort() },
    daySeg () { return [{ value: 'all', label: 'Todos' }].concat(this.days.map(d => ({ value: d, label: this.shortDate(d) }))) },
    filtered () { return this.selectedDay === 'all' ? this.list : this.list.filter(s => s.schedule_date === this.selectedDay) },
    isFiltering () { return this.selectedDay !== 'all' },
    displayList: {
      get () { return this.filtered.slice(0, this.perPage || this.filtered.length) },
      set (val) {
        if (this.isFiltering) return
        this.list = [...val, ...this.list.slice(val.length)]
      }
    }
  },
  mounted () {
    this.load()
    services.listSpeakers().then(res => { this.speakers = res.data.data }).catch(() => {})
    services.listRooms().then(res => { this.rooms = res.data.data }).catch(() => {})
  },
  methods: {
    load () {
      const loader = this.$loading.show()
      services.listSchedules().then(res => { this.list = res.data.data }).catch(() => errorMessage('Falha ao carregar agenda.', 3000)).finally(() => loader.hide())
    },
    shortDate (d) { if (!d) return ''; const dt = new Date(d + 'T00:00:00'); return `${dt.getDate()} ${months[dt.getMonth()]}` },
    dayNum (d) { if (!d) return ''; return new Date(d + 'T00:00:00').getDate() },
    monthAbbr (d) { if (!d) return ''; return months[new Date(d + 'T00:00:00').getMonth()] },
    timeRange (s) {
      const a = s.start_time ? s.start_time.slice(0, 5) : ''
      const b = s.end_time ? s.end_time.slice(0, 5) : ''
      if (a && b) return `${a} / ${b}`
      if (a) return a
      return '—'
    },
    roomName (s) { return s.room ? s.room.name : (s.local || '') },
    selectDay (v) { this.selectedDay = v },
    openCreate () { this.form = empty(); this.view = 'form' },
    openEdit (s) {
      this.form = { ...empty(), ...s, start_time: (s.start_time || '').slice(0, 5), end_time: (s.end_time || '').slice(0, 5), speakers: (s.speakers || []).map(x => x.id) }
      this.view = 'form'
    },
    backToList () { this.view = 'list' },
    payload () {
      return {
        schedule_date: this.form.schedule_date,
        start_time: this.form.start_time || null,
        end_time: this.form.end_time || null,
        title: this.form.title,
        room_id: this.form.room_id || null,
        schedule_type: this.form.schedule_type || null,
        description: this.form.description || null,
        display: this.form.display,
        speakers: this.form.speakers
      }
    },
    async save () {
      if (!this.form.schedule_date || !this.form.title) { errorMessage('Preencha data e título.', 2500); return }
      this.saving = true
      try {
        if (this.form.id) await services.updateSchedule(this.form.id, this.payload())
        else await services.createSchedule(this.payload())
        this.view = 'list'
        successMessage('Sessão salva.', 2000)
        this.load()
      } catch (e) { errorMessage('Falha ao salvar sessão.', 3000) } finally { this.saving = false }
    },
    async toggleVis (s) {
      const next = s.display ? 0 : 1
      try {
        // schedule_date + title obrigatórios; NÃO enviar "speakers" (preserva vínculos).
        await services.updateSchedule(s.id, {
          schedule_date: s.schedule_date,
          start_time: s.start_time || null,
          end_time: s.end_time || null,
          title: s.title,
          room_id: s.room_id || null,
          schedule_type: s.schedule_type || null,
          description: s.description || null,
          display: next
        })
        s.display = next
      } catch (e) { errorMessage('Falha ao alterar visibilidade.', 3000) }
    },
    async remove (s) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover "${s.title}"?`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteSchedule(s.id); this.load() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    },
    async onReorder () {
      try { await services.reorderSchedules(this.list.map(it => ({ id: it.id }))) } catch (e) { errorMessage('Falha ao reordenar.', 3000); this.load() }
    }
  }
}
</script>
